import React from 'react';
import { Todo } from '@/hooks/useTodos';
import { Clock, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface TodoTickerProps {
  todos: Todo[];
}

const TodoTicker = ({ todos }: TodoTickerProps) => {
  const uncompletedTodos = todos.filter(t => !t.completed);

  if (uncompletedTodos.length === 0) return null;

  // Calculate animation duration based on content length
  // Slower speed for fewer items to make it readable
  const duration = Math.max(30, uncompletedTodos.length * 8);

  return (
    <div className="w-full max-w-3xl mx-auto mb-6 h-8 overflow-hidden relative group">
      {/* Gradient masks for smooth fade in/out */}
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-slate-50 dark:from-[#0f172a] to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-slate-50 dark:from-[#0f172a] to-transparent z-10 pointer-events-none"></div>
      
      <div className="flex items-center gap-16 animate-marquee whitespace-nowrap absolute">
        {/* Render multiple times to ensure continuous loop even with few items */}
        {/* We need enough content to fill screen width + buffer for seamless loop */}
        {[...uncompletedTodos, ...uncompletedTodos, ...uncompletedTodos, ...uncompletedTodos].map((todo, index) => (
          <div key={`${todo.id}-${index}`} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 shrink-0">
            {/* Colored circle based on index or hash to give variety */}
            <div className={`w-2 h-2 rounded-full ${['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-blue-500', 'bg-purple-500'][index % 6]}`}></div>
            <span>{todo.text}</span>
            {todo.dueDate && (
              <span className="text-xs text-slate-400 flex items-center gap-1 opacity-70">
                <Clock size={10} />
                {new Date(todo.dueDate).toLocaleDateString()}
              </span>
            )}
          </div>
        ))}
      </div>
      
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-25%); } /* Move by 25% since we quadrupled the content */
        }
        .animate-marquee {
             padding-left: 0;
             animation: marquee ${duration}s linear infinite;
        }
        
        .group:hover .animate-marquee {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default TodoTicker;
